import { useCallback } from 'react';
import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import fetchTickets from './useSearchTickets.request';
import { MessageStatus } from './useSearchTickets.interface';
import { requestedTicketsQueryFn, requestedTicketsQueryKey, selectTicketsByStatus } from './requestedTickets';

import { ApiResponse } from 'modules/IMC/MessageCenter/components/MessageCenterContent/MessageList/MessageList.interface';
import { mergeRecentlyCreatedTickets } from 'services/help-center/recentlyCreatedTickets';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';

/**
 * A unified hook that fetches tickets either by using the requested tickets endpoint
 * or the search endpoint, depending on the presence of `searchQuery`.
 *
 * Behind zendesk_member_projection an unsearched list reads one shared all-status query, so the
 * Open and Closed tabs cost one requested-tickets call between them.
 */
export default function useSearchTickets(
    zdUserId?: number,
    searchQuery?: string,
    status?: MessageStatus[],
    options?: Omit<UseQueryOptions<ApiResponse>, 'queryKey'>
) {
    const isZendeskMemberProjectionEnabled = useStatsigGate(FeatureGates.ZendeskMemberProjection);
    const queryClient = useQueryClient();
    const selectByStatus = useCallback((data: ApiResponse) => selectTicketsByStatus(data, status), [status]);

    const isSharedList = isZendeskMemberProjectionEnabled && !searchQuery && !!zdUserId;
    const listQueryOptions = isSharedList
        ? {
              queryKey: requestedTicketsQueryKey(zdUserId),
              queryFn: requestedTicketsQueryFn(queryClient, zdUserId),
              select: selectByStatus,
              // The gate can resolve on after mount (it targets the member's email, which reaches
              // Statsig after the first render). Keep that user's per-tab list on screen while the
              // shared one loads instead of flashing the skeleton; `select` re-filters it the same
              // way. Never another user's list, and never search results.
              placeholderData: (previous: ApiResponse | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
                  previousQuery?.queryKey[1] === zdUserId && previousQuery.queryKey[2] === '' ? previous : undefined,
          }
        : {
              queryKey: ['messageCenterTickets', zdUserId, searchQuery, status],
              queryFn: async ({ queryKey }: { queryKey: readonly unknown[] }) =>
                  mergeRecentlyCreatedTickets(queryClient, queryKey, await fetchTickets(zdUserId, searchQuery, status)),
          };

    return useQuery<ApiResponse>({
        ...listQueryOptions,
        enabled: !!zdUserId,
        refetchIntervalInBackground: false,
        refetchInterval: 1000 * 60 * 10, // Default to 10 minutes when not specified
        staleTime: 1000 * 60 * 10,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...options,
    });
}
